import React, { useState } from "react";
import "../../../css/AddCenterModal.css";
import api from "../../../api/axiosConfig";
import { showToast } from '../../Shared/Toaster';
import CitySelect from "../../Shared/CitySelect";


interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: (center: any) => void; // optional callback after creation
}

const AddCenterModal: React.FC<Props> = ({ isOpen, onClose, onCreate }) => {
  // Basic info state
  const [name, setName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [telephone1, setTelephone1] = useState("");
  const [telephone2, setTelephone2] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  // Working timings
  const [workingTimings, setWorkingTimings] = useState(
    ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
      (day, i) => ({
        enabled: i < 5, // Mon–Fri enabled by default
        day,
        startTime: "09:00",
        endTime: "17:00",
      })
    )
  );

  // Holidays
  const [holidays, setHolidays] = useState<
    { name: string; day: string; date: string }[]
  >([]);

  // New holiday inputs
  const [holidayName, setHolidayName] = useState("");
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayDay, setHolidayDay] = useState("");

  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // ✅ Submit handler
  const handleAddCenter = async () => {
    if (!name.trim() || !addressLine1.trim() || !city.trim() || !telephone1.trim()) {
      showToast("Please fill in required fields.", 'error');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");

      // ✅ Only include checked working timings
      const filteredWorkingTimings = workingTimings
        .filter((t) => t.enabled)
        .map((t) => ({
          day: t.day,
          startTime: t.startTime,
          endTime: t.endTime,
        }));

      const payload = {
        name,
        centerType: "QAVACH CENTER",
        addressLine1,
        addressLine2,
        city,
        telephone1,
        telephone2,
        latitude: latitude ? parseFloat(latitude) : undefined,
        longitude: longitude ? parseFloat(longitude) : undefined,
        workingTimings: filteredWorkingTimings,
        holidays: holidays.map((h) => ({
          holidayName: h.name,
          day: h.day,
          date: new Date(h.date),
        })),
      };

      const res = await api.center_api.post("/api/v1/centers", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showToast("Center created successfully ✅", 'success');
      if (onCreate) onCreate(res.data);

      // Reset form after submission
      setName("");
      setAddressLine1("");
      setAddressLine2("");
      setCity("");
      setTelephone1("");
      setTelephone2("");
      setLatitude("");
      setLongitude("");
      setWorkingTimings(
        workingTimings.map((w, i) => ({
          ...w,
          enabled: i < 5,
          startTime: "09:00",
          endTime: "17:00",
        }))
      );
      setHolidays([]);

      onClose();
    } catch (err: any) {
      console.error("Error creating center:", err);
      showToast(err.response?.data?.message || "Could not create center ❌", 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update working timing
  const handleTimingChange = (index: number, field: string, value: any) => {
    setWorkingTimings((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };

  // Handle holiday date selection → auto-fill day
  const handleHolidayDateChange = (dateValue: string) => {
    setHolidayDate(dateValue);
    if (dateValue) {
      const day = new Date(dateValue).toLocaleDateString("en-US", {
        weekday: "long",
      });
      setHolidayDay(day);
    } else {
      setHolidayDay("");
    }
  };

  // Add holiday
  const handleAddHoliday = () => {
    if (!holidayName.trim() || !holidayDate) {
      showToast("Please enter holiday name and date.", 'error');
      return;
    }

    const newHoliday = {
      name: holidayName,
      day: holidayDay,
      date: holidayDate,
    };

    setHolidays((prev) => [...prev, newHoliday]);
    setHolidayName("");
    setHolidayDate("");
    setHolidayDay("");
  };

  return (
    <div className="add-center-modal-overlay" onClick={onClose}>
      <div className="add-center-modal-container-add-center" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Center</h2>
          <button className="global-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Basic Information */}
        <section className="add-center-form-section">
          <h3>Basic Information</h3>
          <div className="add-center-form-grid">
            <input
              placeholder="Enter center name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              placeholder="Enter address line 1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
            />
            <input
              placeholder="Enter address line 2 (optional)"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
            <CitySelect value={city} onChange={setCity} placeholder="Select city" />
            <input
              placeholder="Enter telephone number"
              value={telephone1}
              onChange={(e) => setTelephone1(e.target.value)}
            />
            <input
              placeholder="Enter alternate telephone (optional)"
              value={telephone2}
              onChange={(e) => setTelephone2(e.target.value)}
            />
            <input
              placeholder="Latitude"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
            <input
              placeholder="Longitude"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
            />
          </div>
        </section>

        {/* Working Timings */}
        <section className="add-center-form-section">
          <h3>Working Timings</h3>
          <table className="add-center-timings-table">
            <thead>
              <tr>
                <th>Enabled</th>
                <th>Day</th>
                <th>Start Time</th>
                <th>End Time</th>
              </tr>
            </thead>
            <tbody>
              {workingTimings.map((t, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="checkbox"
                      checked={t.enabled}
                      onChange={(e) =>
                        handleTimingChange(i, "enabled", e.target.checked)
                      }
                    />
                  </td>
                  <td>{t.day}</td>
                  <td>
                    <input
                      type="time"
                      value={t.startTime}
                      onChange={(e) =>
                        handleTimingChange(i, "startTime", e.target.value)
                      }
                      disabled={!t.enabled}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={t.endTime}
                      onChange={(e) =>
                        handleTimingChange(i, "endTime", e.target.value)
                      }
                      disabled={!t.enabled}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Holiday Calendar */}
        <section className="add-center-form-section">
          <h3>Holiday Calendar</h3>

          {/* Input Row */}
          <div className="add-center-holiday-inputs">
            <input
              placeholder="Enter holiday name"
              value={holidayName}
              onChange={(e) => setHolidayName(e.target.value)}
            />
            <input type="text" placeholder="Auto-filled" value={holidayDay} readOnly />
            <input
              type="date"
              value={holidayDate}
              onChange={(e) => handleHolidayDateChange(e.target.value)}
            />
            <button type="button" onClick={handleAddHoliday}>
              + Add Holiday
            </button>
          </div>

          {/* Table */}
          <table className="add-center-holiday-table">
            <thead>
              <tr>
                <th>Holiday Name</th>
                <th>Day</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    No holidays added yet
                  </td>
                </tr>
              ) : (
                holidays.map((h, i) => (
                  <tr key={i}>
                    <td>{h.name}</td>
                    <td>{h.day}</td>
                    <td>{new Date(h.date).toLocaleDateString("en-GB")}</td>
                    <td>
                      <button
                        className="add-center-delete-btn"
                        onClick={() =>
                          setHolidays((prev) =>
                            prev.filter((_, idx) => idx !== i)
                          )
                        }
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#d32f2f" viewBox="0 0 24 24">
                      <path d="M3 6h18v2H3V6zm2 3h14l-1.5 12.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 9zm5 2v9h2v-9H10zm4 0v9h2v-9h-2zM9 4V2h6v2h5v2H4V4h5z" />
                    </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {/* Footer buttons */}
        <div className="add-center-modal-footer">
          <button className="add-center-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="add-center-submit-btn" onClick={handleAddCenter}>
            {loading ? "Saving..." : "Add Center"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCenterModal;
